'use client'
import { AppSidebar } from "@/components/app-sidebar"
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar"
import { ReactNode, useEffect, useState } from "react";
import { SearchProvider } from "@/context/SearchContext"
import { NotificationsProvider } from "@/context/NotificationsContext"
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from "@/components/theme-provider";
import { PolicyProvider } from '@/context/PolicyContext'
import { useAuth } from "@/context/AuthContext"
import Notifications from "@/components/Notifications"
import NotificationToastContainer from "@/components/NotificationToastContainer";
import MobileBottomNav from "@/components/MobileBottomNav";
import { AlertCircle, AlertTriangle, Info } from "lucide-react";

interface DashboardLayoutProps {
  children: ReactNode;
}

interface StatusResponse {
  status: string;
  data: {
    maintenance_mode: boolean;
    banner_enabled: boolean;
    banner_type: string;
    banner_message: string;
    banner_platform_only: boolean;
  };
}

// Create a client-side wrapper component for the auth-dependent parts
function AuthenticatedLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<StatusResponse | null>(null);

  useEffect(() => {
    fetch(process.env.NEXT_PUBLIC_API_URL + '/status')
      .then(response => response.json())
      .then(data => {
        const statusResponse: StatusResponse = data;
        setStatus(statusResponse);
      })
      .catch(error => {
        console.error('Error fetching API status:', error);
      });
  }, []);

  return (
    <PolicyProvider user={user}>
      <SidebarProvider>
        <NotificationsProvider>
          <SearchProvider>
            <AppSidebar />
            <SidebarInset className="pb-22 md:pb-0">
              {status?.data && status?.data.banner_enabled && status?.data.banner_message && (
                  <div
                    className={`w-full py-3 px-4 flex items-center justify-center gap-3 shadow-md z-50 ${status.data.banner_type === 'info'
                        ? 'bg-blue-50 dark:bg-blue-950/50 border-b border-blue-200 dark:border-blue-800'
                        : status.data.banner_type === 'warning'
                          ? 'bg-amber-50 dark:bg-amber-950/50 border-b border-amber-200 dark:border-amber-800'
                          : 'bg-red-50 dark:bg-red-950/50 border-b border-red-200 dark:border-red-800'
                      }`}
                  >
                    {status.data.banner_type === 'info' && (
                      <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    )}
                    {status.data.banner_type === 'warning' && (
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    )}
                    {status.data.banner_type === 'error' && (
                      <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    )}
                    <p
                      className={`text-sm font-medium text-center ${status.data.banner_type === 'info'
                          ? 'text-blue-900 dark:text-blue-100'
                          : status.data.banner_type === 'warning'
                            ? 'text-amber-900 dark:text-amber-100'
                            : 'text-red-900 dark:text-red-100'
                        }`}
                    >
                      {status.data.banner_message}
                    </p>
                  </div>
                )}
              {children}
            </SidebarInset>
            <MobileBottomNav />
          </SearchProvider>
          <Notifications />
          <NotificationToastContainer />
        </NotificationsProvider>
      </SidebarProvider>
    </PolicyProvider>
  );
}

export default function Layout({ children }: DashboardLayoutProps) {
  const queryClient = new QueryClient()
  const [isMounted, setIsMounted] = useState(false);

  // Only render auth-dependent components after initial mount
  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <ThemeProvider defaultTheme="system" storageKey="flightdeck-theme">
      <QueryClientProvider client={queryClient}>
        {isMounted ? (
          <AuthenticatedLayout>
            {children}
          </AuthenticatedLayout>
        ) : (
          // Optional loading state while waiting for client-side render
          <div className="flex items-center justify-center h-screen">
            {/* You can add a loading spinner here */}
          </div>
        )}
      </QueryClientProvider>
    </ThemeProvider>
  )
}